import type {
  AnswerProposal,
  ConditionEvidence,
  CreateSpecialistReview,
  ConditionReview,
  CreateDecision,
  DecisionsView,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { refreshLists } from '../../lib/list';

const key = (t: string | undefined, id: string) => ['assessments', t, id] as const;

/** Decisions and conditions (task 64). Asked for once the assessment is past answering. */
export function useDecisions(id: string, enabled = true) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: [...key(tenant?.id, id), 'decisions'],
    queryFn: () => api<DecisionsView>('GET', `/assessments/${id}/decisions`),
    enabled: !!tenant && enabled,
  });
}

function useDecisionMutation<I>(id: string, fn: (input: I) => Promise<DecisionsView>) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: fn,
    onSuccess: (view) => qc.setQueryData([...key(tenant?.id, id), 'decisions'], view),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: key(tenant?.id, id) }),
        qc.invalidateQueries({ queryKey: ['assessments', tenant?.id], exact: true }),
        refreshLists(qc),
      ]),
  });
}

export const useDecide = (id: string) =>
  useDecisionMutation(id, (body: CreateDecision) => api<DecisionsView>('POST', `/assessments/${id}/decisions`, body)); // prettier-ignore
export const useSendEvidence = (id: string) =>
  useDecisionMutation(id, ({ cid, body }: { cid: string; body: ConditionEvidence }) => api<DecisionsView>('POST', `/assessments/${id}/conditions/${cid}/evidence`, body)); // prettier-ignore
export const useReviewCondition = (id: string) =>
  useDecisionMutation(id, ({ cid, body }: { cid: string; body: ConditionReview }) => api<DecisionsView>('POST', `/assessments/${id}/conditions/${cid}/review`, body)); // prettier-ignore

// Task 80: the industry's review steps.
export const useSpecialistReview = (id: string) =>
  useDecisionMutation(id, (body: CreateSpecialistReview) => api<DecisionsView>('POST', `/assessments/${id}/specialist-review`, body)); // prettier-ignore
export const useAnswerProposal = (id: string) =>
  useDecisionMutation(id, (body: AnswerProposal) => api<DecisionsView>('POST', `/assessments/${id}/decisions/proposal`, body)); // prettier-ignore
export const useWithdrawProposal = (id: string) =>
  useDecisionMutation(id, () => api<DecisionsView>('POST', `/assessments/${id}/decisions/proposal/withdraw`)); // prettier-ignore
